import { useRef, useState } from "react";
import { ExternalLink, Link2, Loader2, PlayCircle, Upload } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { errMsg, imgUrl, uploadImage } from "@/lib/api";

export const asMedia = (m) => (typeof m === "string" ? { kind: "image", path: m } : m);

export function MediaTile({ m, className = "h-20 w-20" }) {
  const x = asMedia(m);
  if (x.kind === "video")
    return <video src={imgUrl(x.path)} controls className={`${className} rounded-md border object-cover bg-black`} />;
  if (x.kind === "link")
    return (
      <a href={x.path} target="_blank" rel="noreferrer" className={`${className} rounded-md border flex flex-col items-center justify-center gap-1 p-2 text-[10px] text-muted-foreground hover:text-primary hover:border-primary text-center break-all`}>
        <ExternalLink className="h-4 w-4 shrink-0" /><span className="line-clamp-2">{x.path.replace(/^https?:\/\//, "")}</span>
      </a>
    );
  return <a href={imgUrl(x.path)} target="_blank" rel="noreferrer"><img src={imgUrl(x.path)} alt="" className={`${className} rounded-md border object-cover`} /></a>;
}

export function MediaUploader({ items, onAdd, disabled, testid = "media" }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [link, setLink] = useState("");
  const onFiles = async (files) => {
    setBusy(true);
    try {
      for (const f of files) await onAdd({ kind: f.type.startsWith("video/") ? "video" : "image", path: await uploadImage(f) });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); ref.current.value = ""; }
  };
  const addLink = async () => {
    if (!/^https?:\/\//.test(link.trim())) return toast.error("Paste a full link starting with https://");
    await onAdd({ kind: "link", path: link.trim() });
    setLink("");
  };
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3" data-testid={`${testid}-list`}>
        {(items || []).map((m, i) => <MediaTile key={i} m={m} />)}
        {!disabled && (
          <button type="button" onClick={() => ref.current.click()} disabled={busy} data-testid={`${testid}-upload-button`}
            className="h-20 w-20 rounded-md border border-dashed flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors text-[11px] gap-1">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}{busy ? "Uploading" : <span className="flex items-center gap-0.5">Photo/<PlayCircle className="h-3 w-3" /></span>}
          </button>
        )}
      </div>
      {!disabled && (
        <div className="flex gap-2">
          <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="Or paste a YouTube / Drive / Vimeo link" className="h-8 text-xs" data-testid={`${testid}-link-input`} />
          <Button type="button" size="sm" variant="outline" className="h-8" onClick={addLink} disabled={!link.trim()} data-testid={`${testid}-link-add`}><Link2 className="h-3.5 w-3.5 mr-1" />Add link</Button>
        </div>
      )}
      <input ref={ref} type="file" accept="image/*,video/*" multiple hidden onChange={(e) => onFiles([...e.target.files])} data-testid={`${testid}-file-input`} />
    </div>
  );
}
